import {
  MockAnswerEntry,
  MockAnswerMap,
  MockAttempt,
  MockLocalState,
  MockProgress,
  MockProgressCounts,
  MockQuestion,
  StatementAnswer
} from './mock.models';

export const MOCK_ACTIVE_ATTEMPT_KEY = 'medexam-mock-active-attempt';

export function mockStateStorageKey(attemptId: string): string {
  return 'medexam-mock-attempt:' + attemptId;
}

export function emptyMockState(): MockLocalState {
  return { answers: {}, flags: {}, currentIndex: 0 };
}

export function loadMockState(attemptId: string): MockLocalState {
  try {
    const raw = localStorage.getItem(mockStateStorageKey(attemptId));
    if (!raw) {
      return emptyMockState();
    }
    const parsed = JSON.parse(raw);
    const index = parsed && typeof parsed.currentIndex === 'number' ? parsed.currentIndex : 0;
    return {
      answers: parsed && parsed.answers ? parsed.answers : {},
      flags: parsed && parsed.flags ? parsed.flags : {},
      currentIndex: index >= 0 ? index : 0
    };
  } catch (e) {
    return emptyMockState();
  }
}

export function saveMockState(attemptId: string, state: MockLocalState): void {
  localStorage.setItem(mockStateStorageKey(attemptId), JSON.stringify(state));
}

export function clearMockState(attemptId: string): void {
  localStorage.removeItem(mockStateStorageKey(attemptId));
}

export function loadActiveAttemptId(): string | null {
  return localStorage.getItem(MOCK_ACTIVE_ATTEMPT_KEY);
}

export function saveActiveAttemptId(attemptId: string): void {
  localStorage.setItem(MOCK_ACTIVE_ATTEMPT_KEY, String(attemptId));
}

export function clearActiveAttemptId(): void {
  localStorage.removeItem(MOCK_ACTIVE_ATTEMPT_KEY);
}

export function statementValue(entry: MockAnswerEntry | undefined, index: number): StatementAnswer {
  if (!entry) {
    return null;
  }
  const value = entry[String(index)];
  return value === true || value === false ? value : null;
}

export function questionProgress(question: MockQuestion, answers: MockAnswerMap): MockProgress {
  const entry = answers[String(question.id)];
  let filled = 0;
  for (let i = 0; i < question.options.length; i++) {
    if (statementValue(entry, question.options[i].index) !== null) {
      filled++;
    }
  }
  if (question.options.length === 0 || filled === 0) {
    return 'unanswered';
  }
  if (filled >= question.options.length) {
    return 'answered';
  }
  return 'partial';
}

export function progressCounts(questions: MockQuestion[], state: MockLocalState): MockProgressCounts {
  const counts: MockProgressCounts = { unanswered: 0, partial: 0, answered: 0, flagged: 0 };
  for (let i = 0; i < questions.length; i++) {
    counts[questionProgress(questions[i], state.answers)]++;
    if (state.flags[String(questions[i].id)]) {
      counts.flagged++;
    }
  }
  return counts;
}

export function buildSubmitPayload(questions: MockQuestion[], answers: MockAnswerMap): MockAnswerMap {
  const payload: MockAnswerMap = {};
  for (let i = 0; i < questions.length; i++) {
    const question = questions[i];
    const stored = answers[String(question.id)];
    const entry: MockAnswerEntry = {};
    for (let j = 0; j < question.options.length; j++) {
      const index = question.options[j].index;
      entry[String(index)] = statementValue(stored, index);
    }
    payload[String(question.id)] = entry;
  }
  return payload;
}

export function remainingMilliseconds(expiresAt: string, now: number): number {
  const end = Date.parse(expiresAt);
  if (isNaN(end)) {
    return 0;
  }
  return Math.max(0, end - now);
}

export function formatRemaining(ms: number): string {
  const total = ms <= 0 ? 0 : Math.ceil(ms / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (value: number) => (value < 10 ? '0' + value : String(value));
  if (hours > 0) {
    return pad(hours) + ':' + pad(minutes) + ':' + pad(seconds);
  }
  return pad(minutes) + ':' + pad(seconds);
}

export function isAttemptResumable(attempt: Pick<MockAttempt, 'expires_at' | 'result'>, now: number): boolean {
  if (attempt.result) {
    return false;
  }
  return remainingMilliseconds(attempt.expires_at, now) > 0;
}

export function formatExamTimestamp(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return '';
  }
  const pad = (value: number) => (value < 10 ? '0' + value : String(value));
  return pad(date.getDate()) + '.' + pad(date.getMonth() + 1) + '.' + date.getFullYear()
    + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes());
}

export function formatPercentage(value: number, language: string): string {
  const numeric = Number(value);
  if (!isFinite(numeric)) {
    return '';
  }
  const rounded = Math.round(numeric * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  const localized = language === 'en' ? text : text.replace('.', ',');
  return localized + ' %';
}

export function passGuideText(template: string, threshold: number): string {
  return (template || '').replace('{threshold}', String(threshold));
}

export function areaDisplayName(area: string, translate: (key: string) => string): string {
  const key = 'mockArea_' + area;
  const translated = translate(key);
  if (!translated || translated === key) {
    return (area || '').replace(/[_-]+/g, ' ');
  }
  return translated;
}

export function navigatorSummaryLine(
  template: string,
  answered: number,
  total: number,
  flagged: number
): string {
  return (template || '')
    .replace('{answered}', String(answered))
    .replace('{total}', String(total))
    .replace('{flagged}', String(flagged));
}

export function shortfallLine(
  template: string,
  paper: string,
  area: string,
  available: number,
  requested: number
): string {
  return (template || '')
    .replace('{paper}', paper)
    .replace('{area}', area)
    .replace('{available}', String(available))
    .replace('{requested}', String(requested));
}

export function unwrapMockResult(body: any): any {
  if (body && body.result && typeof body.result.score === 'number') {
    return body.result;
  }
  return body;
}
